import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import AdSense from '../../components/AdSense';

function ThemeCard({ theme }) {
  return (
    <Link
      to={`/blog/${theme.slug}`}
      className="block bg-surface rounded-clay shadow-clay dark:shadow-clay-dark border border-surface p-6 hover:shadow-clay-hover dark:hover:shadow-clay-dark-hover transition-all duration-200 group"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {theme.name}
          </h2>
          <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
            {theme.description}
          </p>
        </div>
        <span className="flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
          {theme.post_count} {theme.post_count === 1 ? 'post' : 'posts'}
        </span>
      </div>
      <div className="mt-4 flex items-center text-sm text-primary-600 dark:text-primary-400 font-medium">
        Browse articles
        <svg className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}

function RecentPostCard({ post }) {
  const dateStr = new Date(post.date).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
  return (
    <Link
      to={`/blog/${post['theme__slug']}/${post.slug}`}
      className="block bg-surface rounded-clay shadow-clay dark:shadow-clay-dark border border-surface p-5 hover:shadow-clay-hover dark:hover:shadow-clay-dark-hover transition-all duration-200 group"
    >
      <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mb-1">
        {post['theme__name']}
      </p>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2">
        {post.title}
      </h3>
      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
        {post.seo_description}
      </p>
      <div className="mt-3 flex items-center gap-3 text-xs text-gray-400 dark:text-gray-500">
        <span>{dateStr}</span>
        <span>·</span>
        <span>{post.read_time} min read</span>
      </div>
    </Link>
  );
}

export default function BlogList() {
  const [themes, setThemes] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/blog/themes/').then(r => r.json()),
      fetch('/api/blog/recent/?limit=6').then(r => r.json()),
    ])
      .then(([t, r]) => { setThemes(t); setRecent(r); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEOHead
        title="Options Trading Blog — Greeks, Strategies & Learnings"
        description="Learn options trading for Indian markets. In-depth guides on Delta, Gamma, Theta, Vega, trading strategies, and IV analysis for NIFTY, Bank Nifty, and NSE stocks."
        keywords="options trading blog india, options greeks explained, nifty options guide, iv rank india"
        canonicalPath="/blog"
      />

      {/* Hero */}
      <div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-5 pb-1">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-neutral-400 dark:text-neutral-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            Back to Home
          </Link>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
            Blog
          </h1>
          <p className="mt-3 text-lg text-gray-500 dark:text-gray-400 max-w-2xl">
            My learnings, opinions, logic and reasoning behind the tools, and hopefully more.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        {loading ? (
          <>
            <section className="mb-14">
              <div className="h-7 w-40 bg-neutral-100 dark:bg-white/8 rounded animate-pulse mb-5" />
              <div className="grid sm:grid-cols-2 gap-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-surface rounded-clay h-32 animate-pulse shadow-clay dark:shadow-clay-dark" />
                ))}
              </div>
            </section>
            <div className="h-24 bg-neutral-100 dark:bg-white/8 rounded animate-pulse mb-14" />
            <section>
              <div className="h-7 w-48 bg-neutral-100 dark:bg-white/8 rounded animate-pulse mb-5" />
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-surface rounded-clay h-36 animate-pulse shadow-clay dark:shadow-clay-dark" />
                ))}
              </div>
            </section>
          </>
        ) : (
          <>
            {/* Themes */}
            <section className="mb-14">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-5">
                Browse by Topic
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {themes.map(theme => (
                  <ThemeCard key={theme.slug} theme={theme} />
                ))}
              </div>
            </section>

            {/* Ad */}
            <AdSense adSlot="" label="Blog Banner" adStyle={{ minHeight: 90 }} />

            {/* Recent Posts */}
            {recent.length > 0 && (
              <section>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-5">
                  Recent Articles
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recent.map(post => (
                    <RecentPostCard key={`${post['theme__slug']}-${post.slug}`} post={post} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}
